import { useId, useState, type ReactNode } from 'react'
import { CheckIcon, ChevronsUpDownIcon } from 'lucide-react'

import { cn } from 'cn'
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '#ui/components/command.tsx'
import { Popover, PopoverContent, PopoverTrigger } from '#ui/components/popover.tsx'

export type ComboboxOption = {
  value: string
  label: string
  /** Shown to the right, muted: a country's code, a calling code. Searchable too. */
  hint?: string
  /** Other words that should find this option — an alternative spelling, a code. */
  keywords?: readonly string[]
}

/**
 * One choice from a list too long to scroll: type to narrow it.
 *
 * A `Select` with 250 countries in it is a list somebody scrolls for thirty seconds; this is the
 * same control with a search box, built from the shadcn `Popover` and `Command` (cmdk), which is
 * where the keyboard behavior comes from — arrows move, Enter chooses, Escape closes and returns
 * focus to the trigger.
 *
 * The trigger takes the `id` and ARIA attributes a `Field` hands its control, so the label names
 * the element a screen reader lands on, exactly as `OptionSelect` does.
 */
export function Combobox({
  value,
  onValueChange,
  options,
  placeholder = 'Choose…',
  searchPlaceholder = 'Search…',
  emptyText = 'Nothing matches.',
  renderValue,
  disabled,
  className,
  contentClassName,
  id,
  'aria-label': ariaLabel,
  'aria-describedby': ariaDescribedBy,
  'aria-invalid': ariaInvalid,
}: {
  value: string
  onValueChange: (value: string) => void
  options: readonly ComboboxOption[]
  placeholder?: string
  searchPlaceholder?: string
  emptyText?: string
  /** What the closed trigger shows for the chosen option. Defaults to its label. */
  renderValue?: (option: ComboboxOption) => ReactNode
  disabled?: boolean | undefined
  className?: string | undefined
  contentClassName?: string | undefined
  id?: string | undefined
  'aria-label'?: string
  'aria-describedby'?: string | undefined
  'aria-invalid'?: boolean
}) {
  const [open, setOpen] = useState(false)
  const listId = useId()
  const selected = options.find((option) => option.value === value)

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          role="combobox"
          aria-expanded={open}
          aria-haspopup="listbox"
          aria-controls={listId}
          disabled={disabled}
          {...(id === undefined ? {} : { id })}
          {...(ariaLabel === undefined ? {} : { 'aria-label': ariaLabel })}
          {...(ariaDescribedBy === undefined ? {} : { 'aria-describedby': ariaDescribedBy })}
          {...(ariaInvalid === undefined ? {} : { 'aria-invalid': ariaInvalid })}
          className={cn(
            'border-input dark:bg-input/30 flex h-9 w-full min-w-0 items-center justify-between gap-2 rounded-md border bg-transparent px-3 py-2 text-left text-sm shadow-xs transition-[color,box-shadow] outline-none',
            'focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px]',
            'aria-invalid:border-destructive aria-invalid:ring-destructive/20 disabled:cursor-not-allowed disabled:opacity-50',
            className,
          )}
        >
          <span className={cn('truncate', selected === undefined && 'text-muted-foreground')}>
            {selected === undefined ? placeholder : (renderValue?.(selected) ?? selected.label)}
          </span>
          <ChevronsUpDownIcon aria-hidden="true" className="size-4 shrink-0 opacity-50" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        id={listId}
        align="start"
        side="bottom"
        className={cn('w-(--radix-popover-trigger-width) min-w-64 p-0', contentClassName)}
        // The list must scroll inside a dialog or sheet. This content is portalled to `body`,
        // outside the dialog, and the dialog's scroll lock cancels every wheel and touch-move that
        // reaches the document from outside it — so they are stopped here, before they get there.
        onWheel={(event) => event.stopPropagation()}
        onTouchMove={(event) => event.stopPropagation()}
      >
        <Command
          // cmdk matches on the item `value`, so the label, hint and keywords are all searchable
          // and the stored value (a code) is the item's identity rather than its search text.
          filter={(itemValue, search, keywords) => {
            const needle = search.trim().toLowerCase()
            if (needle === '') return 1
            const haystack = [itemValue, ...(keywords ?? [])].join(' ').toLowerCase()
            if (haystack.startsWith(needle)) return 1
            return haystack.includes(needle) ? 0.5 : 0
          }}
        >
          <CommandInput placeholder={searchPlaceholder} />
          <CommandList className="overscroll-contain">
            <CommandEmpty>{emptyText}</CommandEmpty>
            <CommandGroup>
              {options.map((option) => (
                <CommandItem
                  key={option.value}
                  value={`${option.label} ${option.value}`}
                  keywords={[option.hint ?? '', ...(option.keywords ?? [])]}
                  onSelect={() => {
                    onValueChange(option.value)
                    setOpen(false)
                  }}
                  className="min-h-9"
                >
                  <CheckIcon
                    aria-hidden="true"
                    className={cn('size-4', option.value === value ? 'opacity-100' : 'opacity-0')}
                  />
                  <span className="truncate">{option.label}</span>
                  {option.hint === undefined ? null : (
                    <span className="text-muted-foreground ml-auto text-xs tabular-nums">
                      {option.hint}
                    </span>
                  )}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  )
}
